import { useState, useEffect } from 'react';
import {
  BarChart3,
  Mail,
  MousePointer,
  Users,
  TrendingUp,
  ExternalLink,
  Loader2,
  RefreshCw,
  Eye,
  AlertCircle
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { getAuthHeaders, adminFetch } from '../../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function NewsletterAnalytics({ newsletter, onClose }) {
  const [analytics, setAnalytics] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [showEngagedOnly, setShowEngagedOnly] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [newsletter.id]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminFetch(
        `${API_BASE}/api/newsletter/admin/newsletter/${newsletter.id}/analytics`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        setError('Failed to load analytics');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    setRecipientsLoading(true);
    try {
      const url = `${API_BASE}/api/newsletter/admin/newsletter/${newsletter.id}/analytics/recipients?engaged_only=${showEngagedOnly}&limit=100`;
      const response = await adminFetch(url, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setRecipients(data.recipients);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
    } finally {
      setRecipientsLoading(false);
    }
  };

  useEffect(() => {
    if (analytics) {
      fetchRecipients();
    }
  }, [analytics, showEngagedOnly]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const truncateUrl = (url, maxLength = 50) => {
    if (!url) return '';
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-400" />
        <p className="text-red-600">{error}</p>
        <Button onClick={fetchAnalytics} variant="outline" className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Analytics: {newsletter.subject}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Sent {formatDate(analytics?.sent_at)}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchAnalytics}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Privacy Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
        <strong>Note:</strong> Open rates may be affected by email client privacy features
        (Apple Mail Privacy Protection, Gmail image proxy). These are estimates based on
        image loading which doesn't capture all opens.
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Mail className="h-4 w-4" />
              Sent
            </div>
            <div className="text-2xl font-bold">{analytics?.total_sent || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <Eye className="h-4 w-4" />
              Opens
            </div>
            <div className="text-2xl font-bold text-green-600">
              {analytics?.unique_opens || 0}
              <span className="text-sm font-normal text-gray-500 ml-1">
                ({analytics?.open_rate || 0}%)
              </span>
            </div>
            <div className="text-xs text-gray-400">
              {analytics?.total_opens || 0} total opens
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <MousePointer className="h-4 w-4" />
              Clicks
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {analytics?.unique_clicks || 0}
              <span className="text-sm font-normal text-gray-500 ml-1">
                ({analytics?.click_rate || 0}%)
              </span>
            </div>
            <div className="text-xs text-gray-400">
              {analytics?.total_clicks || 0} total clicks
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
              <TrendingUp className="h-4 w-4" />
              Click-to-Open
            </div>
            <div className="text-2xl font-bold text-purple-600">
              {analytics?.click_to_open_rate || 0}%
            </div>
            <div className="text-xs text-gray-400">
              Of those who opened
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Clicked Links */}
      {analytics?.top_links?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ExternalLink className="h-4 w-4" />
              Top Clicked Links
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.top_links.map((link, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline truncate block"
                      title={link.url}
                    >
                      {truncateUrl(link.url)}
                    </a>
                  </div>
                  <div className="flex items-center gap-4 ml-4 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      <strong>{link.clicks}</strong> clicks
                    </span>
                    <span className="text-gray-400">
                      ({link.unique_clicks} unique)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recipient Engagement */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" />
              Recipient Engagement
            </CardTitle>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={showEngagedOnly}
                onChange={(e) => setShowEngagedOnly(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              Show engaged only
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {recipientsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : recipients.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {showEngagedOnly
                ? 'No engaged recipients yet'
                : 'No recipient data available'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2">Recipient</th>
                    <th className="text-center py-2 px-2">Opens</th>
                    <th className="text-center py-2 px-2">Clicks</th>
                    <th className="text-left py-2 px-2">Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((recipient) => (
                    <tr key={recipient.recipient_id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-2 px-2">
                        <div>
                          <div className="font-medium">{recipient.name || 'No name'}</div>
                          <div className="text-gray-500 text-xs">{recipient.email}</div>
                        </div>
                      </td>
                      <td className="text-center py-2 px-2">
                        {recipient.opens > 0 ? (
                          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                            {recipient.opens}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="text-center py-2 px-2">
                        {recipient.clicks > 0 ? (
                          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                            {recipient.clicks}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-gray-500 text-xs">
                        {recipient.last_clicked
                          ? formatDate(recipient.last_clicked)
                          : recipient.last_opened
                          ? formatDate(recipient.last_opened)
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
